import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import { mediaUrl } from '../../utils/media';
import ProfilePictureUpload from '../ProfilePictureUpload';
import ProfilePictureZoom from '../ProfilePictureZoom';
import CredentialManager from './sections/CredentialManager';
import VerificationSidebar from './sections/VerificationSidebar';
import {
  User, Mail, MapPin, Phone, Star, Calendar, Truck, Shield,
  Clock, Award, Route, Fuel, FileText, Briefcase, ChevronRight,
  ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';

const DriverProfile = ({ profile, user, onUpdate }) => {
  const [badges, setBadges] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifiedVisible, setVerifiedVisible] = useState(profile?.verified_visible !== false);
  const [ratingsVisible, setRatingsVisible] = useState(profile?.ratings_visible !== false);
  const [openToWork, setOpenToWork] = useState(profile?.open_to_work === true);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    phone: profile?.phone || '',
    location: profile?.location || '',
    bio: profile?.bio || '',
    license_type: profile?.license_type || '',
    experience_years: profile?.experience_years || '',
    endorsements: profile?.endorsements || '',
    availability: profile?.availability || 'full_time'
  });

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const res = await api.get('/badges/list');
      setBadges(res.data);
    } catch (error) {
      // Badge fetch handled silently
    }
  };

  const toggleVisibility = async (field) => {
    try {
      const { data } = await api.put(`/profile/toggle-visibility?field=${field}`);
      if (field === 'verified_visible') setVerifiedVisible(data[field]);
      else if (field === 'ratings_visible') setRatingsVisible(data[field]);
      else if (field === 'open_to_work') setOpenToWork(data[field]);
      const labels = { verified_visible: 'Verified badge', ratings_visible: 'Ratings', open_to_work: 'Open to Work' };
      toast.success(`${labels[field]} ${data[field] ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/profile/me', formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const verifiedBadges = badges.filter(b => b.status === 'verified').length;
  const completeness = [profile?.phone, profile?.location, profile?.bio, profile?.license_type, profile?.experience_years].filter(Boolean).length;
  // Reputation starts at 100, decreases only when negative ratings come in
  // Badges and profile completeness give a small bonus on top of the base
  const reputationScore = 100;

  const availabilityLabel = {
    full_time: 'Full Time', part_time: 'Part Time', weekends: 'Weekends Only', on_call: 'On Call'
  };

  return (
    <div className="space-y-6">
      {/* Open to Work Banner */}
      {openToWork && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-green-500 rounded-xl px-5 py-3 flex items-center justify-between shadow-sm"
          data-testid="open-to-work-banner">
          <div className="flex items-center gap-3">
            <Briefcase className="h-5 w-5 text-white" />
            <div>
              <p className="text-white font-semibold text-sm">Open to Work</p>
              <p className="text-green-100 text-xs">Carriers and dispatchers can see you're available for jobs</p>
            </div>
          </div>
          <button onClick={() => toggleVisibility('open_to_work')}
            className="text-xs text-white/80 hover:text-white bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition-colors font-medium"
            data-testid="dismiss-open-to-work">
            Turn Off
          </button>
        </motion.div>
      )}

      {/* Hero Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-800"></div>
        <div className="absolute inset-0 opacity-[0.07]" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20.5z' fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`}}></div>

        <div className="relative px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="flex flex-col items-center gap-2" data-testid="driver-profile-picture-area">
                <ProfilePictureZoom src={user?.profile_picture} alt={profile?.name || 'Driver'} size="w-24 h-24">
                  <ProfilePictureUpload currentPicture={user?.profile_picture} onUploadSuccess={() => { if (onUpdate) onUpdate(); }} />
                </ProfilePictureZoom>
                <div className="flex items-center gap-2">
                  {user?.truck_picture ? (
                    <ProfilePictureZoom src={user.truck_picture} alt="My Truck" size="w-9 h-9">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 cursor-zoom-in hover:bg-white/20 transition-colors" data-testid="truck-picture-badge">
                        <img src={mediaUrl(user.truck_picture)} alt="Truck" className="w-5 h-5 rounded-full object-cover border border-white/40" />
                        <span className="text-[10px] font-semibold text-white/80 tracking-wider uppercase">My Truck</span>
                      </div>
                    </ProfilePictureZoom>
                  ) : null}
                  <label className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 cursor-pointer hover:bg-white/20 transition-colors" data-testid="truck-upload-profile-btn">
                    <Truck className="h-3.5 w-3.5 text-blue-300" />
                    <span className="text-[10px] font-semibold text-white/80 tracking-wider uppercase">{user?.truck_picture ? 'Change' : 'Add Truck'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append('file', file);
                          await api.post('/users/truck-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          toast.success('Truck picture uploaded!');
                          if (onUpdate) onUpdate();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to upload');
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
              <div>
                <span className="inline-block px-3 py-1 bg-blue-500/20 backdrop-blur-sm rounded-full text-xs font-semibold text-blue-300 border border-blue-400/30 mb-2 tracking-wider uppercase">
                  Driver
                </span>
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="driver-profile-title">
                  {profile?.name || 'Driver Profile'}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-slate-400 text-sm">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile?.location || 'Location not set'}</span>
                  {profile?.experience_years && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{profile.experience_years} yrs exp</span>}
                </div>
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-2">
                <button onClick={() => toggleVisibility('open_to_work')}
                  className={`flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm rounded-lg border transition-colors ${openToWork ? 'bg-green-500/20 border-green-400/40 hover:bg-green-500/30' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}
                  data-testid="toggle-open-to-work-btn">
                  {openToWork ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  <span className="text-xs text-white font-medium">Open to Work</span>
                </button>
                <button onClick={() => toggleVisibility('verified_visible')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                  data-testid="toggle-verified-btn">
                  {verifiedVisible ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  <span className="text-xs text-white font-medium">Verified {verifiedVisible ? 'On' : 'Off'}</span>
                </button>
                <button onClick={() => toggleVisibility('ratings_visible')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                  data-testid="toggle-ratings-btn">
                  {ratingsVisible ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                  <span className="text-xs text-white font-medium">Ratings {ratingsVisible ? 'On' : 'Off'}</span>
                </button>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-5 border border-white/10 min-w-[160px]" data-testid="reputation-score">
              <div className="text-center">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Reputation</p>
                <div className="text-4xl font-bold text-white">{reputationScore}</div>
                <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-400 rounded-full transition-all duration-500" style={{ width: `${reputationScore}%` }}></div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{verifiedBadges} badge{verifiedBadges !== 1 ? 's' : ''} verified</p>
              </div>
            </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" /> Driver Details
              </h2>
              <button onClick={() => setEditing(!editing)} className="text-sm text-blue-500 hover:text-blue-600 font-medium" data-testid="edit-driver-profile-btn">
                {editing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Full Name</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-testid="driver-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-testid="driver-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Location</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Houston, TX" data-testid="driver-location-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">License Type</label>
                    <select value={formData.license_type} onChange={(e) => setFormData({...formData, license_type: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-testid="driver-license-select">
                      <option value="">Select License</option>
                      <option value="standard">Standard Driver License</option>
                      <option value="class_b">Class B CDL</option>
                      <option value="class_c">Class C CDL</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Years of Experience</label>
                    <input type="number" value={formData.experience_years} onChange={(e) => setFormData({...formData, experience_years: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="5" data-testid="driver-experience-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Availability</label>
                    <select value={formData.availability} onChange={(e) => setFormData({...formData, availability: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-testid="driver-availability-select">
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="weekends">Weekends Only</option>
                      <option value="on_call">On Call</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Endorsements</label>
                  <input type="text" value={formData.endorsements} onChange={(e) => setFormData({...formData, endorsements: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Hazmat, Tanker, Doubles/Triples" data-testid="driver-endorsements-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">About Me</label>
                  <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Tell dispatchers and carriers about your experience..." data-testid="driver-bio-input" />
                </div>
                <button type="submit" disabled={loading} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50" data-testid="save-driver-profile-btn">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: User, label: 'Name', value: profile?.name },
                    { icon: Mail, label: 'Email', value: profile?.email || user?.email },
                    { icon: Phone, label: 'Phone', value: profile?.phone },
                    { icon: MapPin, label: 'Location', value: profile?.location },
                    { icon: Shield, label: 'License', value: profile?.license_type?.replace('_', ' ') },
                    { icon: Calendar, label: 'Experience', value: profile?.experience_years ? `${profile.experience_years} years` : null },
                    { icon: Clock, label: 'Availability', value: availabilityLabel[profile?.availability] || profile?.availability },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50/80">
                      <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
                        <p className="text-sm font-medium text-slate-800">{value || 'Not set'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {profile?.endorsements && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-2">Endorsements</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.endorsements.split(',').map((e) => (
                        <span key={e.trim()} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-100">{e.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.bio && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-1">About</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{profile.bio}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Credentials (TWIC, Hazmat, etc.) */}
          <CredentialManager badges={badges} onBadgesChange={fetchBadges} />

          {/* Driver Quick Links */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-slate-900 rounded-xl p-6 text-white"
          >
            <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link to="/jobs" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5" data-testid="driver-quick-jobs">
                <Briefcase className="h-5 w-5 text-blue-400" />
                <div><p className="text-sm font-medium">Find Jobs</p><p className="text-[11px] text-slate-400">Browse opportunities</p></div>
                <ChevronRight className="h-4 w-4 text-slate-500 ml-auto" />
              </Link>
              <Link to="/resources" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5" data-testid="driver-quick-resources">
                <FileText className="h-5 w-5 text-green-400" />
                <div><p className="text-sm font-medium">Resources</p><p className="text-[11px] text-slate-400">Guides & training</p></div>
                <ChevronRight className="h-4 w-4 text-slate-500 ml-auto" />
              </Link>
              <Link to="/referrals" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5" data-testid="driver-quick-referrals">
                <Award className="h-5 w-5 text-amber-400" />
                <div><p className="text-sm font-medium">Referrals</p><p className="text-[11px] text-slate-400">Earn rewards</p></div>
                <ChevronRight className="h-4 w-4 text-slate-500 ml-auto" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <VerificationSidebar profile={profile} badges={badges} color="blue" />

          {/* Driver Stats */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Truck className="h-4 w-4 text-blue-500" /> Driver Snapshot
            </h3>
            <div className="space-y-2.5">
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Credentials</span>
                <span className="text-sm font-semibold text-slate-800">{badges.length}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Verified</span>
                <span className="text-sm font-semibold text-green-600">{verifiedBadges}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 bg-slate-50 rounded-lg">
                <span className="text-xs text-slate-500">Profile</span>
                <span className="text-sm font-semibold text-slate-800">{Math.round(completeness / 5 * 100)}% complete</span>
              </div>
            </div>
          </motion.div>

          {/* Tips */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
            className="bg-blue-50 border border-blue-100 rounded-xl p-5"
          >
            <h3 className="text-sm font-bold text-blue-900 mb-3">Driver Tips</h3>
            <div className="space-y-2.5 text-xs text-blue-800">
              {badges.some(b => b.status === 'pending') && (
                <p className="p-2 bg-yellow-50 rounded-lg border border-yellow-200 text-yellow-800">Pending credentials are awaiting admin review.</p>
              )}
              {!profile?.endorsements && <p className="p-2 bg-blue-100/60 rounded-lg">Add your endorsements (Hazmat, Tanker) to unlock more job matches.</p>}
              {completeness < 4 && <p className="p-2 bg-blue-100/60 rounded-lg">Complete your profile to increase your reputation score.</p>}
              {completeness >= 4 && verifiedBadges > 0 && <p className="p-2 bg-green-50 rounded-lg border border-green-200 text-green-800">Your profile is looking strong. Keep your credentials current.</p>}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DriverProfile;
